import React from 'react';

export interface SubjectRingItem {
  name: string;
  percent: number | null;
  attempts: number;
}

interface SubjectsRingProps {
  subjects: SubjectRingItem[];
  size?: number;
  showLegend?: boolean;
  label?: string;
}

// Distinct colors per subject, cycled.
const PALETTE = ['#7C3AED', '#F97316', '#2563EB', '#16A34A', '#DB2777', '#0EA5E9', '#CA8A04', '#9333EA'];

/**
 * A concentric multi-ring: one colored arc per subject (nested), with the overall average % in
 * the center. Plays the same role as ProgressRing but for the whole Subjects group.
 */
const SubjectsRing: React.FC<SubjectsRingProps> = ({ subjects, size = 132, showLegend = false, label = 'Subjects' }) => {
  const withData = subjects.filter((s) => s.percent !== null && !Number.isNaN(s.percent as number));
  const n = Math.max(withData.length, 1);

  // Overall average, weighted by attempts (falls back to a simple mean).
  const totalAttempts = withData.reduce((s, x) => s + (x.attempts || 0), 0);
  const overall =
    withData.length === 0
      ? null
      : totalAttempts > 0
      ? withData.reduce((s, x) => s + (x.percent as number) * (x.attempts || 0), 0) / totalAttempts
      : withData.reduce((s, x) => s + (x.percent as number), 0) / withData.length;

  // `outer` is the outermost edge any stroke may reach (kept inside the viewBox to avoid clipping).
  const outer = size / 2 - 2;
  const centerHole = size * 0.30;
  const band = (outer - centerHole) / n;      // radial width allotted to each ring
  const ringStroke = Math.max(3, band - 3);   // leave a small gap between bands
  // Centerline radius for ring i — offset inward by half a band so the stroke stays within `outer`.
  const ringRadius = (i: number) => outer - band / 2 - i * band;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          {withData.length === 0 && (
            <circle cx={size / 2} cy={size / 2} r={ringRadius(0)} fill="none" stroke="#EEF0F4" strokeWidth={ringStroke} />
          )}
          {withData.map((s, i) => {
            const r = ringRadius(i);
            const circ = 2 * Math.PI * r;
            const clamped = Math.max(0, Math.min(100, s.percent as number));
            const offset = circ - (clamped / 100) * circ;
            const color = PALETTE[i % PALETTE.length];
            return (
              <g key={s.name + i}>
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EEF0F4" strokeWidth={ringStroke} />
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={r}
                  fill="none"
                  stroke={color}
                  strokeWidth={ringStroke}
                  strokeLinecap="round"
                  strokeDasharray={circ}
                  strokeDashoffset={offset}
                  style={{ transition: 'stroke-dashoffset 0.7s ease' }}
                />
              </g>
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {overall !== null ? (
            <>
              <span className="text-2xl font-extrabold font-display text-gray-800">{Math.round(overall)}%</span>
              <span className="text-[10px] text-gray-400 leading-none">overall</span>
            </>
          ) : (
            <span className="text-xs text-gray-400 text-center px-2">No data yet</span>
          )}
        </div>
      </div>
      <span className="text-sm font-bold text-gray-700">{label}</span>
      {showLegend && withData.length > 0 && (
        <div className="flex flex-col gap-1 w-full px-2">
          {withData.map((s, i) => (
            <div key={s.name + i} className="flex items-center gap-2 text-xs">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
              <span className="text-gray-600 truncate flex-1">{s.name}</span>
              <span className="font-semibold text-gray-700">{Math.round(s.percent as number)}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SubjectsRing;
